<script setup>
import { useI18n } from 'vue-i18n'
import { ref, computed } from 'vue'

const { tm, t, locale } = useI18n()

// Define all tabs
const tabs = [
  { id: 1, key: 'term1', title: t('terms.term1.title') },
  { id: 2, key: 'term2', title: t('terms.term2.title') },
  { id: 3, key: 'term3', title: t('terms.term3.title') },
  { id: 4, key: 'term4', title: t('terms.term4.title') },
  { id: 5, key: 'term5', title: t('terms.term5.title') },
  { id: 6, key: 'term6', title: t('terms.term6.title') }
]

const activeTab = ref(1)
const setTab = (id) => (activeTab.value = id)

// Return sections for the active tab
const currentSections = computed(() => {
  const tab = tabs.find((t) => t.id === activeTab.value)
  return tab ? tm(`terms.${tab.key}.sections`) : []
})
</script>

<template>
  <div class="min-h-screen  text-white flex flex-col">
    <!-- Header -->
    <header class="py-4 text-center border-b border-white/10  sticky top-0 z-30">
      <h1 class="text-xl font-bold tracking-wide">{{ t('terms.1') }}</h1>
    </header>

    <!-- Tabs -->
    <nav class="sticky top-[64px] z-20  border-b border-white/10 overflow-x-auto hide-scrollbar">
      <ul class="flex items-center space-x-2 px-3 py-2">
        <li
          v-for="tab in tabs"
          :key="tab.id"
          @click="setTab(tab.id)"
          class="flex-shrink-0 cursor-pointer px-4 py-2 rounded-full text-sm transition-all duration-200 whitespace-nowrap"
          :class="[
            activeTab === tab.id
              ? 'bg-white text-black font-semibold'
              : 'bg-white/10 text-white/70 hover:bg-white/20'
          ]"
        >
          {{ tab.title }}
        </li>
      </ul>
    </nav>

    <!-- Tab content -->
    <main class="flex-1 px-5 py-6 text-[15px] leading-relaxed text-white/80">
      <transition name="fade" mode="out-in">
        <section :key="activeTab" class="space-y-6">
          <p
            v-if="!currentSections || !currentSections.length"
            class="text-center text-red-400 text-sm"
          >
            ⚠️ No content found for locale "{{ locale }}"
          </p>

          <div
            v-for="(section, idx) in currentSections"
            :key="idx"
            class="bg-[#1e1e1e] rounded-xl p-4 border border-white/10 shadow-sm"
          >
            <h2 class="text-lg font-semibold text-white mb-1">{{ section.title }}</h2>
            <p v-if="section.subtitle" class="text-sm text-gray-400 mb-3">{{ section.subtitle }}</p>

            <!-- Lists -->
            <ul
              v-if="Array.isArray(section.content) && typeof section.content[0] === 'string'"
              class="list-disc ml-5 space-y-1 text-gray-200"
            >
              <li v-for="(item, i) in section.content" :key="i">{{ item }}</li>
            </ul>

            <!-- Term/definition pairs -->
            <div v-else class="mt-2 space-y-2 text-gray-200">
              <div
                v-for="(pair, i) in section.content"
                :key="i"
                class="border-b border-white/10 pb-2 last:border-0"
              >
                <p class="font-semibold text-white">{{ pair.term }}</p>
                <p class="text-sm text-gray-400">{{ pair.definition }}</p>
              </div>
            </div>
          </div>
        </section>
      </transition>
    </main>
  </div>
</template>

<style scoped>
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
section { animation: fadeIn 0.3s ease-in-out; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
